import { t } from "@travily/i18n";
import type Badge from "@travily/types/models/BadgesRow";
import { useBreakpoint } from "@ui/hooks/use-breakpoint";
import { tv } from "@ui/lib/tv";
import { useCallback, useLayoutEffect, useRef, useState } from "react";
import { filterBadgesWithArtwork, resolveBadgeImage } from "@/images/badges";
import passportBook from "@/images/passport/passport-desktop.webp";
import { BadgeHoverCard } from "./BadgeHoverCard";
import { BadgeImage, EmptyBadgePlaceholder, passportSlot } from "./PassportSlot";

export interface PassportBadgesProps {
  badges: Badge[];
  totalCount: number;
  /** Opens the full passport ("view all") experience from the +N overflow slot. */
  onViewAll?: () => void;
}

const passportBadges = tv({
  slots: {
    root: "flex items-center justify-center gap-4 rounded-3xl bg-neutral-25 py-3 lg:gap-6 lg:py-5",
    count: "rotate-1 font-semibold text-neutral-500 text-xl",
  },
});

const styles = passportBadges();
const slotStyles = passportSlot();

// Warm the heavy book backdrop at most once per session — the HTTP cache dedupes the
// fetch, but this avoids re-allocating an Image on every hover/focus of the +N slot.
let bookWarmed = false;

function useSlotCount(
  containerRef: React.RefObject<HTMLDivElement | null>,
  badgeRef: React.RefObject<HTMLElement | null>,
) {
  const [slotCount, setSlotCount] = useState(6);

  useLayoutEffect(() => {
    const container = containerRef.current;
    const badge = badgeRef.current;
    if (!container || !badge) return;

    function update() {
      const c = containerRef.current;
      const b = badgeRef.current;
      if (!c || !b) return;

      const containerWidth = c.clientWidth;
      const badgeWidth = b.offsetWidth;
      const gap = Number.parseFloat(getComputedStyle(c).columnGap) || 0;

      if (badgeWidth === 0) return;

      const maxSlots = Math.floor((containerWidth + gap) / (badgeWidth + gap));
      const next = Math.max(1, maxSlots - 1);
      setSlotCount((prev) => (prev === next ? prev : next));
    }

    update();

    const observer = new ResizeObserver(update);
    observer.observe(container);

    return () => observer.disconnect();
  }, [containerRef, badgeRef]);

  return slotCount;
}

function PlaceholderSlot({
  children,
  innerRef,
}: {
  children?: React.ReactNode;
  innerRef?: React.RefObject<HTMLElement | null>;
}) {
  return (
    <div
      ref={innerRef as React.RefObject<HTMLDivElement | null>}
      className={`${slotStyles.slot()} ${slotStyles.placeholderChrome()}`}
    >
      {children ?? <EmptyBadgePlaceholder />}
    </div>
  );
}

export function PassportBadges({ badges, totalCount, onViewAll }: PassportBadgesProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const badgeRef = useRef<HTMLElement>(null);
  const slotCount = useSlotCount(containerRef, badgeRef);

  // Drop badges whose artwork the client bundle can't resolve, then pair each with
  // its url (filterBadgesWithArtwork guarantees a hit, so the cast is safe).
  const resolved = filterBadgesWithArtwork(badges).map((badge) => ({
    badge,
    url: resolveBadgeImage(badge.image_key) as string,
  }));

  const visible = resolved.slice(0, slotCount);
  const emptySlotCount = slotCount - visible.length;
  const overflow = Math.max(0, totalCount - visible.length);

  // Warm the heavy book backdrop the moment the user signals intent to open the
  // passport (hover/focus on the +N control), so it's decoded before the modal
  // mounts. Only on desktop — mobile opens the notepad (different art), so fetching
  // the book there is wasted bytes. `useBreakpoint("md")` is the same split
  // BadgesModal uses to pick its surface, so the prefetch can't disagree with it.
  const isDesktop = useBreakpoint("md");
  const prefetchBook = useCallback(() => {
    if (bookWarmed || !isDesktop) return;
    bookWarmed = true;
    new Image().src = passportBook;
  }, [isDesktop]);

  return (
    <div ref={containerRef} className={styles.root()}>
      {visible.map(({ badge, url }) => (
        <BadgeHoverCard key={badge.id} badge={badge} triggerClassName={slotStyles.slot()}>
          <BadgeImage url={url} alt={badge.label} imageKey={badge.image_key} />
        </BadgeHoverCard>
      ))}
      {Array.from({ length: emptySlotCount }, (_, i) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: static empty placeholders that never reorder
        <PlaceholderSlot key={`empty-${i}`} />
      ))}
      <PlaceholderSlot innerRef={badgeRef}>
        {overflow > 0 ? (
          // Always a <button> so the element type is stable across SSR/hydration
          // (the server renders desktop=true); when there's no handler (mobile,
          // where the passport modal is deferred) it's simply disabled — a no-op.
          <button
            type="button"
            disabled={onViewAll === undefined}
            onClick={onViewAll}
            onMouseEnter={prefetchBook}
            onFocus={prefetchBook}
            aria-label={t("passport.badges.view_all", { count: overflow })}
            className="flex size-full items-center justify-center rounded-[inherit] outline-none transition-colors focus-visible:ring-2 focus-visible:ring-neutral-400 enabled:cursor-pointer enabled:hover:bg-neutral-50"
          >
            {/* eslint-disable-next-line i18next/no-literal-string -- visual "+" prefix is not translatable */}
            <span className={styles.count()}>+{overflow}</span>
          </button>
        ) : (
          <EmptyBadgePlaceholder />
        )}
      </PlaceholderSlot>
    </div>
  );
}
